import React, { useState, useEffect } from 'react';
import { theme } from '../styles/theme';
import { Header } from '../components/Header';
import { Footer } from '../components/Footer';
import { Card } from '../components/Card';
import { Button } from '../components/Button';
import { Badge } from '../components/Badge';
import { Input } from '../components/Input';
import { Select } from '../components/Select';
import { API_ENDPOINTS, getAuthHeaders } from '../config/api';

interface Manuscript {
  id: string;
  title: string;
  author: string;
  genre: string;
  wordCount: number;
  synopsis: string;
  contentWarnings?: string;
  status: string;
  postedDate: string;
  deadline: string | null;
  firstPagesAvailable: boolean;
  targetAudience?: string;
  completionStatus?: string;
}

export const DiscoverManuscriptsPage = () => {
  const [viewMode, setViewMode] = useState('grid'); // 'grid' or 'list'
  const [searchQuery, setSearchQuery] = useState('');
  const [genreFilter, setGenreFilter] = useState('all');
  const [wordCountFilter, setWordCountFilter] = useState('all');
  const [contentWarningFilter, setContentWarningFilter] = useState('all');
  const [sortBy, setSortBy] = useState('newest');
  const [manuscripts, setManuscripts] = useState<Manuscript[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // Fetch manuscripts on component mount
  useEffect(() => {
    fetchManuscripts();
  }, []);

  const fetchManuscripts = async () => {
    setLoading(true);
    setError('');

    try {
      const response = await fetch(`${API_ENDPOINTS.manuscripts.list}?seekingBeta=true`, {
        headers: getAuthHeaders(),
      });

      const data = await response.json();

      if (!response.ok) {
        setError(data.error || 'Failed to fetch manuscripts');
        setLoading(false);
        return;
      }

      // Transform backend data to frontend format
      const transformedManuscripts = data.manuscripts.map((m: any) => ({
        id: m.id,
        title: m.title,
        author: m.author?.displayName || 'Unknown Author',
        genre: formatGenre(m.genre || m.primaryGenre),
        wordCount: m.wordCount,
        synopsis: m.blurb || m.synopsis,
        contentWarnings: m.contentWarnings || '',
        status: 'seeking',
        postedDate: new Date(m.createdAt).toISOString().split('T')[0],
        deadline: m.betaDeadline ? new Date(m.betaDeadline).toISOString().split('T')[0] : null,
        firstPagesAvailable: !!m.excerpt || !!m.firstPages,
        targetAudience: m.targetAudience,
        completionStatus: m.completionStatus,
      }));

      setManuscripts(transformedManuscripts);
      setLoading(false);
    } catch (err) {
      console.error('Fetch manuscripts error:', err);
      setError('Network error. Please check your connection and try again.');
      setLoading(false);
    }
  };

  // Helper function to format genre enum to readable string
  const formatGenre = (genre: string) => {
    const genreMap: Record<string, string> = {
      FANTASY: 'Fantasy',
      SCIENCE_FICTION: 'Science Fiction',
      MYSTERY: 'Mystery',
      THRILLER: 'Thriller',
      ROMANCE: 'Romance',
      LITERARY_FICTION: 'Literary Fiction',
      HISTORICAL_FICTION: 'Historical Fiction',
      HORROR: 'Horror',
      YOUNG_ADULT: 'Young Adult',
      MIDDLE_GRADE: 'Middle Grade',
      CONTEMPORARY: 'Contemporary',
      PARANORMAL: 'Paranormal',
      DYSTOPIAN: 'Dystopian',
      OTHER: 'Other',
    };
    return genreMap[genre] || genre;
  };

  // Filter options
  const genres = ['All Genres', 'Fantasy', 'Science Fiction', 'Mystery', 'Romance', 'Horror', 'Historical Fiction', 'Literary Fiction'];
  const wordCountRanges = [
    { value: 'all', label: 'All Word Counts' },
    { value: '0-70000', label: 'Under 70k' },
    { value: '70000-90000', label: '70k - 90k' },
    { value: '90000-110000', label: '90k - 110k' },
    { value: '110000+', label: 'Over 110k' },
  ];
  const contentWarnings = [
    { value: 'all', label: 'All Content' },
    { value: 'none', label: 'No Warnings' },
    { value: 'violence', label: 'Exclude Violence' },
    { value: 'sexual', label: 'Exclude Sexual Content' },
    { value: 'profanity', label: 'Exclude Profanity' },
  ];
  const sortOptions = [
    { value: 'newest', label: 'Newest First' },
    { value: 'oldest', label: 'Oldest First' },
    { value: 'wordcount-high', label: 'Word Count (High to Low)' },
    { value: 'wordcount-low', label: 'Word Count (Low to High)' },
    { value: 'deadline', label: 'Deadline (Soonest First)' },
  ];

  // Filter manuscripts
  const filteredManuscripts = manuscripts.filter(manuscript => {
    // Search filter
    const matchesSearch = manuscript.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         manuscript.author.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         manuscript.synopsis.toLowerCase().includes(searchQuery.toLowerCase());

    // Genre filter
    const matchesGenre = genreFilter === 'all' || manuscript.genre === genreFilter;

    // Word count filter
    let matchesWordCount = true;
    if (wordCountFilter !== 'all') {
      if (wordCountFilter === '0-70000') {
        matchesWordCount = manuscript.wordCount < 70000;
      } else if (wordCountFilter === '70000-90000') {
        matchesWordCount = manuscript.wordCount >= 70000 && manuscript.wordCount <= 90000;
      } else if (wordCountFilter === '90000-110000') {
        matchesWordCount = manuscript.wordCount >= 90000 && manuscript.wordCount <= 110000;
      } else if (wordCountFilter === '110000+') {
        matchesWordCount = manuscript.wordCount > 110000;
      }
    }

    // Content warning filter
    let matchesContentWarning = true;
    if (contentWarningFilter !== 'all') {
      const warnings = manuscript.contentWarnings || '';
      if (contentWarningFilter === 'none') {
        matchesContentWarning = warnings.includes('None') || warnings === '';
      } else if (contentWarningFilter === 'violence') {
        matchesContentWarning = !warnings.includes('Violence') && !warnings.includes('Gore');
      } else if (contentWarningFilter === 'sexual') {
        matchesContentWarning = !warnings.includes('Sexual Content');
      } else if (contentWarningFilter === 'profanity') {
        matchesContentWarning = !warnings.includes('Profanity');
      }
    }

    return matchesSearch && matchesGenre && matchesWordCount && matchesContentWarning;
  });

  // Sort manuscripts
  const sortedManuscripts = [...filteredManuscripts].sort((a, b) => {
    switch (sortBy) {
      case 'newest':
        return new Date(b.postedDate).getTime() - new Date(a.postedDate).getTime();
      case 'oldest':
        return new Date(a.postedDate).getTime() - new Date(b.postedDate).getTime();
      case 'wordcount-high':
        return b.wordCount - a.wordCount;
      case 'wordcount-low':
        return a.wordCount - b.wordCount;
      case 'deadline':
        if (!a.deadline) return 1;
        if (!b.deadline) return -1;
        return new Date(a.deadline).getTime() - new Date(b.deadline).getTime();
      default:
        return 0;
    }
  });

  const ManuscriptGridCard = ({ manuscript }: { manuscript: Manuscript }) => (
    <Card>
      <h3 style={{
        fontSize: '20px',
        fontWeight: '500',
        marginBottom: '8px',
        color: theme.colors.textPrimary,
      }}>
        {manuscript.title}
      </h3>
      <p style={{
        fontSize: '15px',
        color: theme.colors.textSecondary,
        marginBottom: '12px',
      }}>
        by {manuscript.author}
      </p>

      <div style={{
        display: 'flex',
        flexWrap: 'wrap',
        gap: '8px',
        marginBottom: '12px',
      }}>
        <Badge>{manuscript.genre}</Badge>
        <Badge variant="default">{manuscript.wordCount.toLocaleString()} words</Badge>
        {manuscript.targetAudience && (
          <Badge variant="default">{manuscript.targetAudience}</Badge>
        )}
      </div>

      <p style={{
        fontSize: '14px',
        lineHeight: '1.6',
        color: theme.colors.textPrimary,
        marginBottom: '12px',
        display: '-webkit-box',
        WebkitLineClamp: 3,
        WebkitBoxOrient: 'vertical',
        overflow: 'hidden',
      }}>
        {manuscript.synopsis}
      </p>

      {manuscript.contentWarnings && manuscript.contentWarnings !== 'None' && (
        <div style={{
          fontSize: '13px',
          color: theme.colors.textTertiary,
          marginBottom: '12px',
        }}>
          <strong>Content Warnings:</strong> {manuscript.contentWarnings}
        </div>
      )}

      <div style={{
        fontSize: '13px',
        color: theme.colors.textTertiary,
        marginBottom: '4px',
      }}>
        {manuscript.completionStatus && (
          <div style={{ marginBottom: '4px' }}>
            <strong>Status:</strong> {manuscript.completionStatus}
          </div>
        )}
        <div>
          <strong>Deadline:</strong> {manuscript.deadline || 'Not specified'}
        </div>
      </div>

      <div style={{
        display: 'flex',
        gap: '8px',
      }}>
        <Button
          size="small"
          onClick={() => window.location.href = `/manuscripts/${manuscript.id}/preview`}
        >
          View Details
        </Button>
        <Button
          size="small"
          variant="outline"
          onClick={() => window.location.href = `/manuscripts/${manuscript.id}/preview`}
        >
          Read Sample
        </Button>
      </div>
    </Card>
  );

  const ManuscriptListItem = ({ manuscript }: { manuscript: Manuscript }) => (
    <Card>
      <div style={{
        display: 'flex',
        gap: '24px',
        alignItems: 'flex-start',
      }}>
        <div style={{ flex: 1 }}>
          <h3 style={{
            fontSize: '22px',
            fontWeight: '500',
            marginBottom: '8px',
            color: theme.colors.textPrimary,
          }}>
            {manuscript.title}
          </h3>
          <p style={{
            fontSize: '16px',
            color: theme.colors.textSecondary,
            marginBottom: '12px',
          }}>
            by {manuscript.author}
          </p>

          <div style={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: '8px',
            marginBottom: '12px',
          }}>
            <Badge>{manuscript.genre}</Badge>
            <Badge variant="default">{manuscript.wordCount.toLocaleString()} words</Badge>
            {manuscript.targetAudience && (
              <Badge variant="default">{manuscript.targetAudience}</Badge>
            )}
          </div>

          <p style={{
            fontSize: '15px',
            lineHeight: '1.6',
            color: theme.colors.textPrimary,
            marginBottom: '12px',
          }}>
            {manuscript.synopsis}
          </p>

          {manuscript.contentWarnings && manuscript.contentWarnings !== 'None' && (
            <div style={{
              fontSize: '13px',
              color: theme.colors.textTertiary,
              marginBottom: '8px',
            }}>
              <strong>Content Warnings:</strong> {manuscript.contentWarnings}
            </div>
          )}

          <div style={{
            fontSize: '13px',
            color: theme.colors.textTertiary,
          }}>
            {manuscript.completionStatus && (
              <>
                <strong>Status:</strong> {manuscript.completionStatus} • {' '}
              </>
            )}
            <strong>Posted:</strong> {manuscript.postedDate} • <strong>Deadline:</strong> {manuscript.deadline || 'Not specified'}
          </div>
        </div>

        <div style={{
          display: 'flex',
          flexDirection: 'column',
          gap: '8px',
          minWidth: '140px',
        }}>
          <Button
            size="small"
            onClick={() => window.location.href = `/manuscripts/${manuscript.id}/preview`}
          >
            View Details
          </Button>
          <Button
            size="small"
            variant="outline"
            onClick={() => window.location.href = `/manuscripts/${manuscript.id}/preview`}
          >
            Read Sample
          </Button>
        </div>
      </div>
    </Card>
  );

  return (
    <div style={{
      minHeight: '100vh',
      backgroundColor: theme.colors.background,
      fontFamily: theme.fonts.primary,
    }}>
      <Header />

      <main style={{
        maxWidth: '1200px',
        margin: '0 auto',
        padding: '60px 40px',
      }}>
        {/* Header */}
        <div style={{ marginBottom: '40px' }}>
          <h1 style={{
            fontSize: '40px',
            fontWeight: '400',
            marginBottom: '8px',
            color: theme.colors.textPrimary,
          }}>
            Discover Manuscripts
          </h1>
          <p style={{
            fontSize: '18px',
            color: theme.colors.textSecondary,
          }}>
            Find your next beta reading project
          </p>
        </div>

        {/* Search and Filters */}
        <Card style={{ marginBottom: '32px' }}>
          <div style={{
            display: 'flex',
            flexDirection: 'column',
            gap: '20px',
          }}>
            {/* Search Bar */}
            <Input
              type="text"
              placeholder="Search by title, author, or keywords..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />

            {/* Filters Row */}
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
              gap: '16px',
            }}>
              <Select
                label="Genre"
                value={genreFilter}
                onChange={(e) => setGenreFilter(e.target.value)}
                options={genres.map(genre => ({
                  value: genre === 'All Genres' ? 'all' : genre,
                  label: genre,
                }))}
              />

              <Select
                label="Word Count"
                value={wordCountFilter}
                onChange={(e) => setWordCountFilter(e.target.value)}
                options={wordCountRanges}
              />

              <Select
                label="Content"
                value={contentWarningFilter}
                onChange={(e) => setContentWarningFilter(e.target.value)}
                options={contentWarnings}
              />

              <Select
                label="Sort By"
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value)}
                options={sortOptions}
              />
            </div>
          </div>
        </Card>

        {/* Error Message */}
        {error && (
          <Card style={{ marginBottom: '24px' }}>
            <div style={{
              textAlign: 'center',
              padding: '40px',
              color: theme.colors.error,
            }}>
              <p style={{
                fontSize: '18px',
                marginBottom: '20px',
              }}>
                {error}
              </p>
              <Button onClick={fetchManuscripts}>
                Try Again
              </Button>
            </div>
          </Card>
        )}

        {/* Loading State */}
        {loading && !error && (
          <Card>
            <div style={{
              textAlign: 'center',
              padding: '40px',
            }}>
              <p style={{
                fontSize: '18px',
                color: theme.colors.textSecondary,
              }}>
                Loading manuscripts...
              </p>
            </div>
          </Card>
        )}

        {/* View Toggle and Results Count */}
        {!loading && !error && (
          <>
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginBottom: '24px',
            }}>
              <div style={{
                fontSize: '16px',
                color: theme.colors.textSecondary,
              }}>
                {sortedManuscripts.length} manuscript{sortedManuscripts.length !== 1 ? 's' : ''} found
              </div>

              <div style={{
                display: 'flex',
                gap: '8px',
              }}>
                <Button
                  size="small"
                  variant={viewMode === 'grid' ? 'primary' : 'outline'}
                  onClick={() => setViewMode('grid')}
                >
                  Grid
                </Button>
                <Button
                  size="small"
                  variant={viewMode === 'list' ? 'primary' : 'outline'}
                  onClick={() => setViewMode('list')}
                >
                  List
                </Button>
              </div>
            </div>

            {/* Results */}
            {sortedManuscripts.length === 0 ? (
          <Card>
            <div style={{
              textAlign: 'center',
              padding: '60px 20px',
            }}>
              <p style={{
                fontSize: '18px',
                color: theme.colors.textSecondary,
                marginBottom: '16px',
              }}>
                No manuscripts found matching your criteria
              </p>
              <Button
                variant="outline"
                onClick={() => {
                  setSearchQuery('');
                  setGenreFilter('all');
                  setWordCountFilter('all');
                  setContentWarningFilter('all');
                }}
              >
                Clear Filters
              </Button>
            </div>
          </Card>
        ) : (
          <div style={viewMode === 'grid' ? {
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fill, minmax(340px, 1fr))',
            gap: '24px',
          } : {
            display: 'flex',
            flexDirection: 'column',
            gap: '16px',
          }}>
            {sortedManuscripts.map(manuscript =>
              viewMode === 'grid' ? (
                <ManuscriptGridCard key={manuscript.id} manuscript={manuscript} />
              ) : (
                <ManuscriptListItem key={manuscript.id} manuscript={manuscript} />
              )
            )}
          </div>
        )}
          </>
        )}

        {/* Tips Card */}
        {!loading && !error && (
          <Card style={{
          backgroundColor: theme.colors.backgroundAlt,
          marginTop: '40px',
        }}>
          <h3 style={{
            fontSize: '18px',
            fontWeight: '500',
            marginBottom: '12px',
            color: theme.colors.textPrimary,
          }}>
            Beta Reading Tips
          </h3>
          <ul style={{
            margin: 0,
            paddingLeft: '20px',
            fontSize: '15px',
            lineHeight: '1.8',
            color: theme.colors.textSecondary,
          }}>
            <li>Only apply for manuscripts you're genuinely interested in reading</li>
            <li>Review content warnings carefully before applying</li>
            <li>Be realistic about your reading speed and availability</li>
            <li>Read the sample pages to ensure the writing style appeals to you</li>
            <li>Communicate promptly if you need to withdraw from a commitment</li>
            <li>Provide thoughtful, constructive feedback that helps the writer improve</li>
          </ul>
        </Card>
        )}
      </main>

      <Footer />
    </div>
  );
};
